import { enqueueSnackbar } from 'notistack';
import { action, autorun, makeObservable } from 'mobx';
import React from 'react';

import injectStores from '../RootStore/injectStores';
import NotificationButtonInstallPwa from './NotificationButton.InstallPwa';
import NotificationButtonAppUpdate from './NotificationButton.AppUpdate';
import NotificationStore from 'stores/NotificationStore';
import ViewStore from 'stores/ViewStore';

interface INotification {
  key: string;
  message: string;
  options?: object;
  value?: string;
}

interface IStandardNotification {
  key: string;
  value: string;
}

interface IHandleNotifications {
  key: string;
  message: string;
  options?: {
    variant?: 'default' | 'error' | 'success' | 'info' | 'warning' | undefined;
    persist?: boolean;
    autoHideDuration?: number;
    action?: React.ReactNode;
    onClick?: () => void;
    onExited?: () => void;
    onClose?: (
      event: React.SyntheticEvent<unknown, Event> | null,
      reason: string
    ) => void;
  };
  value?: string;
}

interface INotificationHandlerProps {
  notificationStore?: NotificationStore;
  viewStore?: ViewStore;
  onEnqueueSnackbar: (
    message: string,
    options?: {
      key?: string | number;
      variant?: string;
      persist?: boolean;
      autoHideDuration?: number;
      action?: React.ReactNode;
      onClick?: () => void;
      onExited?: () => void;
      onClose?: (event: React.SyntheticEvent<unknown>, reason: string) => void;
    }
  ) => string;
}

class NotificationHandler extends React.PureComponent<INotificationHandlerProps> {
  disposeNotification;
  disposeStandardNotification;
  constructor(props: INotificationHandlerProps) {
    super(props);
    makeObservable(this, {
      handleNotifications: action,
      // handleStandardNotifications: action
    });
    if (props.notificationStore) {
      const notificationStore = props.notificationStore;
      this.disposeNotification = autorun(() => {
        const notifications = notificationStore.notifications;

        notifications.slice().map((notification: INotification) => {
          return this.handleNotifications(notification);
        });
      });

      this.disposeStandardNotification = autorun(() => {
        const { standardNotifications } = notificationStore;

        standardNotifications.slice().map((standardNotification) => {
          return this.handleStandardNotifications(standardNotification);
        });
      });
    }
  }

  handleStandardNotifications = (notification: IStandardNotification) => {
    const notificationStore = this.props.notificationStore;
    const { displayed } = notificationStore;
    // Do nothing if snackbar is already displayed
    if (displayed.find((n: INotification) => n.key === notification.key))
      return;
    // Display snackbar using notistack

    if (notification.value === 'Test') {
      return this.handleNotifications({
        ...notification,
        message: 'Test',
      });
    }

    if (notification.value === 'Install iOS') {
      const { viewStore } = this.props;
      const appTitle = viewStore?.appTitle;
      const appName = viewStore?.appName;
      const message = `To install the ${
        appTitle || appName || 'App'
      }(Safari only), tap share then 'Add to Home Screen'`;
      const options = { autoHideDuration: 1500 };
      return this.handleNotifications({
        ...notification,
        message: message,
        options: options,
      });
    }
    if (notification.value === 'Install PWA') {
      const { viewStore } = this.props;
      const appTitle = viewStore?.appTitle;
      const appName = viewStore?.appName;
      const message = `Install ${appTitle || appName || 'App'}?`;
      const options = {
        autoHideduration: 15000,
        action: <NotificationButtonInstallPwa />,
      };
      return this.handleNotifications({
        ...notification,
        message: message,
        options: options,
      });
    }
    if (this.props.viewStore && notification.value === 'App Update') {
      const message = 'Update available!';
      const options = {
        action: <NotificationButtonAppUpdate />,
        variant: 'info' as const,
        persist: true,
      };

      return this.handleNotifications({
        ...notification,
        message: message,
        options: options,
      });
    }
    if (notification.value === 'Autodesk Login') {
      const message = 'Logged Into Autodesk';
      const options = { variant: 'success' as const, autoHideduration: 3000 };
      return this.handleNotifications({
        ...notification,
        message: message,
        options: options,
      });
    }
    return this.handleNotifications({
      ...notification,
      message: notification.value,
    });
  };

  handleNotifications = (notification: IHandleNotifications) => {
    const notificationStore = this.props.notificationStore;
    const {
      storeDisplayed,
      displayed,
      removeNotification,
      addNotificationRefKey,
    } = notificationStore;
    // Do nothing if snackbar is already displayed
    const alreadyDisplayed = displayed.find(
      (n: INotification) => n.key === notification.key
    );
    if (alreadyDisplayed) return;
    // Display snackbar using notistack

    const keyFromNotification = enqueueSnackbar(
      notification.message,
      notification.options
    );
    // Keep track of snackbars that we've displayed
    storeDisplayed(notification);
    if (keyFromNotification)
      addNotificationRefKey(notification.key, String(keyFromNotification));
    // Dispatch action to remove snackbar from mobx store

    return removeNotification(notification.key);
  };

  componentWillUnmount() {
    this.disposeNotification && this.disposeNotification();
    this.disposeStandardNotification && this.disposeStandardNotification();
  }

  render() {
    return null;
  }
}

export default injectStores(NotificationHandler);
